import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { ListaSolicitudes, Solicitud } from '../../../api/ops/clientesPrivacidad';
import { ErrorApi } from '../../../api/problema';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { rolesDe } from '../../../logica/acceso';
import { estadoDeError } from '../../../logica/estados';
import { diaMes, diasHasta } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { PanelPlazos } from './PanelPlazos';
import { PanelRegistrar } from './PanelRegistrar';
import { PanelSolicitud } from './PanelSolicitud';
import { AYUDA, ESTADO, FILTROS, PERMISO_RGPD, TEXTOS, TIPO, VACIA, VIA, grupoDe, vence, type Grupo } from './textos';
import '../../../componentes/apartado.css';
import './proteccion.css';

/** Nombre de cada cliente de la página (con su ficha; uno anonimizado ya no tiene). */
type Nombres = Readonly<Record<string, string>>;

const ANONIMIZADO = 'Cliente anonimizado';

function nombresDe(ops: Operaciones, negocio: string, ids: readonly string[]): Promise<Record<string, string>> {
    return Promise.all(
        ids.map((id) =>
            ops
                .getCustomer(negocio, id)
                .then((r): [string, string] => [id, r.datos.status === 'anonymized' ? ANONIMIZADO : r.datos.display_name])
                .catch((e: unknown): [string, string] => [id, e instanceof ErrorApi && e.codigo === 'not_found' ? ANONIMIZADO : '']),
        ),
    ).then((pares) => Object.fromEntries(pares));
}

/**
 * Protección de datos (2.7): las solicitudes con su plazo (aviso a 10 días o menos), filtradas por Abiertas · Resueltas ·
 * Rechazadas sin cifras (G140). Quién la atiende sale de `handled_by` (G150): las de Reservaria, solo como información.
 * Todo funciona en solo lectura, salvo cambiar los plazos de conservación (obligación legal, `x-read-only-mode: allowed`).
 */
export function Proteccion(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const atender = permisos.puede(PERMISO_RGPD);
    const [filtro, setFiltro] = useState(0);
    const grupo: Grupo = FILTROS[filtro]?.grupo ?? 'abiertas';
    const pag = usePaginacionCursor();
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaSolicitudes | null; nombres: Nombres; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<Solicitud | null>(null);
    const [registrar, setRegistrar] = useState(false);
    const [plazos, setPlazos] = useState(false);
    const { reiniciar } = pag;
    const vistaActual = `${grupo}|${pag.cursor ?? ''}`;

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!atender) {
            return;
        }
        let vivo = true;
        const vista = `${grupo}|${pag.cursor ?? ''}`;
        // Abiertas son varios estados: se piden todas y se quitan las resueltas y las rechazadas.
        const estado = grupo === 'resueltas' ? 'resolved' : grupo === 'rechazadas' ? 'rejected' : null;
        ops.listPrivacyRequests(negocio, estado, { cursor: pag.cursor })
            .then((l) => {
                const datos = { ...l, data: l.data.filter((s) => grupoDe(s.status) === grupo) };
                return nombresDe(ops, negocio, [...new Set(datos.data.map((s) => s.customer_id))]).then((nombres) => ({ datos, nombres }));
            })
            .then(({ datos, nombres }) => {
                if (vivo) {
                    setCargada({ vista, lista: datos, nombres, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, nombres: c?.nombres ?? {}, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, grupo, pag.cursor, intento, atender]);

    const acciones: AccionCabecera[] = [];
    if (atender) {
        acciones.push({ clave: 'plazos', texto: 'Plazos de conservación', icono: 'clock', secundaria: true, onClick: () => { setPlazos(true); } });
        acciones.push({ clave: 'registrar', texto: 'Registrar solicitud', icono: 'plus', onClick: () => { setRegistrar(true); } });
    }
    useCabecera({ ayuda: atender ? AYUDA.lista : [], acciones });

    if (!atender) {
        return <EstadoSinPermiso seccion="Protección de datos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const lista = cargada?.vista === vistaActual ? cargada.lista : null;
    const nombres = cargada?.nombres ?? {};
    const error = cargada?.vista === vistaActual ? cargada.error : null;

    const contenido = (() => {
        if (error !== null && lista === null) {
            return <EstadoError seccion="las solicitudes" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
        }
        if (lista === null) {
            return <EstadoCargando seccion="solicitudes" />;
        }
        if (lista.data.length === 0) {
            return (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{VACIA[grupo].titulo}</strong>
                    <span>{VACIA[grupo].texto}</span>
                </div>
            );
        }
        const ahora = new Date();
        // Lo que vence antes, arriba.
        const filas = grupo === 'abiertas' ? [...lista.data].sort((a, b) => vence(a).localeCompare(vence(b))) : lista.data;
        return (
            <>
                <div className="rv-ca-lista" role="list" aria-label="Solicitudes de protección de datos">
                    {filas.map((s) => {
                        const nombre = nombres[s.customer_id] ?? '';
                        const abiertaS = grupoDe(s.status) === 'abiertas';
                        const dias = diasHasta(vence(s), ahora);
                        // El aviso de plazo solo en las que atiende el negocio (G150): las de Reservaria no se marcan como urgentes.
                        const tarde = abiertaS && s.handled_by === 'business' && dias <= 10;
                        const tono = tarde ? (dias <= 3 ? 'rv-pr-plazo--rojo' : 'rv-pr-plazo--aviso') : '';
                        const linea = [
                            s.scope === 'account' ? 'Pidió borrar su Cuenta Reservaria' : VIA[s.channel],
                            s.handled_by === 'system' ? TEXTOS.laAtiendeReservaria : '',
                            s.status === 'in_progress' || s.status === 'extended' || s.status === 'scheduled' || s.status === 'blocked' ? ESTADO[s.status][0] : '',
                        ]
                            .filter((t) => t !== '')
                            .join(' · ');
                        return (
                            <div
                                key={s.id}
                                role="listitem"
                                className="rv-ca-fila rv-ca-fila--abre rv-pr-fila"
                                tabIndex={0}
                                aria-label={`${nombre}: ${TIPO[s.type].nombre}`}
                                onClick={() => { setAbierta(s); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setAbierta(s);
                                    }
                                }}
                            >
                                <span className={`rv-pr-plazo ${tono}`}>
                                    {abiertaS ? (
                                        <>
                                            <strong className={dias === 1 ? 'rv-pr-plazo__manana' : undefined}>{dias === 1 ? 'Mañana' : String(Math.max(0, dias))}</strong>
                                            <span>{dias === 1 ? `vence ${diaMes(vence(s), ahora)}` : `días · ${diaMes(vence(s), ahora)}`}</span>
                                        </>
                                    ) : (
                                        <>
                                            <strong className="rv-pr-plazo__manana">{diaMes(s.resolved_at, ahora)}</strong>
                                            <span>{s.status === 'rejected' ? 'rechazada' : 'resuelta'}</span>
                                        </>
                                    )}
                                </span>
                                <span className="rv-ca-fila__texto">
                                    <strong>{nombre}</strong>
                                    <span className="rv-pr-linea">
                                        <Icono nombre={TIPO[s.type].icono} />
                                        <b>{TIPO[s.type].nombre}</b>
                                        {linea !== '' && <span>{` · ${linea}`}</span>}
                                    </span>
                                </span>
                                <Icono nombre="chevron-right" className="rv-ca-chevron" />
                            </div>
                        );
                    })}
                </div>
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            </>
        );
    })();

    return (
        <div className="rv-ca-apartado">
            <Filtros
                etiqueta="Estado de las solicitudes"
                items={FILTROS}
                valor={filtro}
                onCambio={(i) => {
                    setFiltro(i);
                    reiniciar();
                }}
            />
            {soloLectura && (
                <p className="rv-ca-nota">
                    <Icono nombre="shield-check" />
                    <span>{TEXTOS.soloLecturaLista}</span>
                </p>
            )}
            {contenido}
            <PanelSolicitud
                ops={ops}
                negocio={negocio}
                nombreNegocio={nombreNegocio}
                movil={movil}
                soloLectura={soloLectura}
                solicitud={abierta}
                nombre={abierta === null ? '' : (nombres[abierta.customer_id] ?? '')}
                onCerrar={() => { setAbierta(null); }}
                onCambio={(s) => {
                    setAbierta(s);
                    recargar();
                }}
            />
            <PanelRegistrar
                ops={ops}
                negocio={negocio}
                nombreNegocio={nombreNegocio}
                movil={movil}
                abierto={registrar}
                onCerrar={() => { setRegistrar(false); }}
                onRegistrada={() => {
                    setRegistrar(false);
                    if (grupo !== 'abiertas') {
                        setFiltro(0);
                        reiniciar();
                    }
                    recargar();
                }}
            />
            <PanelPlazos ops={ops} negocio={negocio} movil={movil} soloLectura={soloLectura} abierto={plazos} onCerrar={() => { setPlazos(false); }} />
        </div>
    );
}
